import { ACTIVITY_LIMITS, type ActivityProps } from './activity.model'

const ENCODER = new TextEncoder()
const PROP_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/
/** U+0000, or a UTF-16 surrogate without its partner (PostgreSQL rejects both). */
const BAD_CHARS_RE = /\u0000|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g

function cleanString(v: string): string {
  const s = v.replace(BAD_CHARS_RE, '?')
  return s.length > ACTIVITY_LIMITS.maxPropString ? s.slice(0, ACTIVITY_LIMITS.maxPropString) : s
}

/** A primitive the server accepts, or `undefined` for anything else (dropped). */
function cleanPrimitive(v: unknown): string | number | boolean | null | undefined {
  if (typeof v === 'string') return cleanString(v)
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined
  if (typeof v === 'boolean' || v === null) return v
  return undefined
}

function cleanValue(v: unknown): unknown {
  if (Array.isArray(v)) {
    const out: (string | number)[] = []
    for (const el of v.slice(0, ACTIVITY_LIMITS.maxPropArray)) {
      const c = cleanPrimitive(el)
      if (typeof c === 'string' || typeof c === 'number') out.push(c)
    }
    return out
  }
  if (typeof v === 'object' && v !== null) {
    const out: Record<string, string | number | boolean | null> = {}
    let n = 0
    for (const [k, inner] of Object.entries(v)) {
      if (n >= ACTIVITY_LIMITS.maxPropsKeys) break
      const c = cleanPrimitive(inner)
      if (c === undefined || !PROP_KEY_RE.test(k)) continue
      out[k] = c
      n++
    }
    return out
  }
  return cleanPrimitive(v)
}

/**
 * Mirrors the server's props rules (contract §2.2) so a batch is never rejected
 * for size or shape: <= 16 keys matching the key regex; values a string (<= 64
 * chars), finite number, boolean, null, an array of <= 16 strings/numbers or a
 * flat object of <= 16 primitives; <= 1 024 bytes serialised (keys dropped from
 * the end until it fits); U+0000 / lone surrogates replaced by "?". `undefined`
 * values are omitted. Pure; the caller decides whether to warn.
 */
export function sanitizeProps(props: ActivityProps | undefined): ActivityProps {
  if (!props) return {}
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || !PROP_KEY_RE.test(k)) continue
    if (Object.keys(out).length >= ACTIVITY_LIMITS.maxPropsKeys) break
    const c = cleanValue(v)
    if (c !== undefined) out[k] = c
  }
  const keys = Object.keys(out)
  while (keys.length > 0 && ENCODER.encode(JSON.stringify(out)).length > ACTIVITY_LIMITS.maxPropsBytes) {
    delete out[keys.pop()!]
  }
  return out as ActivityProps
}
